import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Package, Loader2, ChevronDown, ChevronRight, Globe,
  RefreshCcw, ExternalLink, ArrowUpDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import {
  alibabaApi,
  type InsightsSummaryItem,
  type ProductListItem,
} from "@/api/alibaba";

// ── Page component ──

export default function InsightsSummary() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedTag, setExpandedTag] = useState<string | null>(null);
  const [sortField, setSortField] = useState<keyof InsightsSummaryItem>("total_products");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const { data: summaryData, isLoading } = useQuery({
    queryKey: ["insights-summary"],
    queryFn: async () => {
      const res = await alibabaApi.getInsightsSummary();
      return res.data as InsightsSummaryItem[];
    },
  });

  const toggleSort = (field: keyof InsightsSummaryItem) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const sorted = [...(summaryData || [])].sort((a, b) => {
    const av = a[sortField];
    const bv = b[sortField];
    if (typeof av === "number" && typeof bv === "number") {
      return sortDir === "asc" ? av - bv : bv - av;
    }
    return sortDir === "asc"
      ? String(av ?? "").localeCompare(String(bv ?? ""))
      : String(bv ?? "").localeCompare(String(av ?? ""));
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>Loading insights summary...</span>
      </div>
    );
  }

  const SortHeader = ({ field, label }: { field: keyof InsightsSummaryItem; label: string }) => (
    <TableHead
      className="cursor-pointer hover:text-foreground select-none whitespace-nowrap"
      onClick={() => toggleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field
          ? (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)
          : <ArrowUpDown className="h-3 w-3 opacity-30" />}
      </span>
    </TableHead>
  );

  return (
    <div className="p-6 space-y-6 max-w-[1400px]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="h-6 w-6" />
            Product Insights Summary
          </h1>
          <p className="text-sm text-muted-foreground">
            {sorted.length} keywords tracked / click to expand product list
          </p>
        </div>
      </div>

      {/* Summary table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Keywords Overview ({sorted.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <SortHeader field="tag" label="Keyword" />
                  <SortHeader field="total_products" label="Products" />
                  <SortHeader field="parsed" label="Parsed" />
                  <SortHeader field="progress_pct" label="Progress" />
                  <SortHeader field="avg_price_min" label="Avg Price" />
                  <TableHead>Countries</TableHead>
                  <SortHeader field="trade_assurance_pct" label="TA%" />
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map(item => (
                  <SummaryRow
                    key={item.tag}
                    item={item}
                    isExpanded={expandedTag === item.tag}
                    onToggle={() => setExpandedTag(prev => prev === item.tag ? null : item.tag)}
                    onViewReport={() => navigate(`/alibaba-insights/${encodeURIComponent(item.tag)}`)}
                    queryClient={queryClient}
                  />
                ))}
                {sorted.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      No product insights data yet. Run product parsing first.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Summary Row ──

function SummaryRow({
  item,
  isExpanded,
  onToggle,
  onViewReport,
  queryClient,
}: {
  item: InsightsSummaryItem;
  isExpanded: boolean;
  onToggle: () => void;
  onViewReport: () => void;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const progressColor = item.progress_pct >= 80
    ? "bg-green-500"
    : item.progress_pct >= 50
      ? "bg-yellow-500"
      : "bg-red-500";

  const parseMutation = useMutation({
    mutationFn: () => alibabaApi.parseProducts(item.tag),
    onSuccess: (res) => {
      toast.success(`Parsed ${res.data?.parsed ?? 0} products`);
      queryClient.invalidateQueries({ queryKey: ["insights-summary"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={onToggle}>
        <TableCell className="w-8 px-2">
          {isExpanded
            ? <ChevronDown className="h-3.5 w-3.5" />
            : <ChevronRight className="h-3.5 w-3.5" />}
        </TableCell>
        <TableCell className="font-medium text-sm max-w-[200px] truncate" onClick={e => e.stopPropagation()}>
          <Link
            to={`/alibaba-insights/${encodeURIComponent(item.tag)}`}
            className="text-blue-600 hover:text-blue-800 hover:underline"
          >
            {item.tag}
          </Link>
        </TableCell>
        <TableCell>
          <Badge variant="secondary" className="text-xs">{item.total_products}</Badge>
        </TableCell>
        <TableCell className="text-sm">
          <span className="text-green-600">{item.parsed}</span>
          {item.partial > 0 && <span className="text-yellow-600 ml-1">+{item.partial}</span>}
          {item.failed > 0 && <span className="text-red-600 ml-1">/{item.failed}F</span>}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2 min-w-[120px]">
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
              <div className={`h-full ${progressColor} transition-all`} style={{ width: `${item.progress_pct}%` }} />
            </div>
            <span className="text-xs text-muted-foreground w-8">{item.progress_pct}%</span>
          </div>
        </TableCell>
        <TableCell className="text-sm whitespace-nowrap">
          {item.avg_price_min != null
            ? `$${item.avg_price_min.toFixed(2)}${item.avg_price_max != null ? ` - $${item.avg_price_max.toFixed(2)}` : ''}`
            : '—'}
        </TableCell>
        <TableCell>
          <div className="flex gap-1">
            {item.top_countries.map(c => (
              <Badge key={c} variant="outline" className="text-[10px] px-1">{c}</Badge>
            ))}
          </div>
        </TableCell>
        <TableCell className="text-sm">{item.trade_assurance_pct}%</TableCell>
        <TableCell onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost" size="sm" className="h-7 text-xs"
              onClick={onViewReport}
              title="View full report"
            >
              <ExternalLink className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost" size="sm" className="h-7 text-xs"
              onClick={() => parseMutation.mutate()}
              disabled={parseMutation.isPending}
              title="Re-parse products"
            >
              {parseMutation.isPending
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <RefreshCcw className="h-3 w-3" />}
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={9} className="p-0 bg-muted/20">
            <ExpandedProductList tag={item.tag} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ── Expanded Product List (paginated) ──

function ExpandedProductList({ tag }: { tag: string }) {
  const [page, setPage] = useState(1);
  const limit = 50;

  const { data, isLoading } = useQuery({
    queryKey: ["products-by-tag", tag, page],
    queryFn: async () => {
      const res = await alibabaApi.getProductsByTag(tag, page, limit);
      return res.data!;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6 gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading products...</span>
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-muted-foreground">
        No parsed products yet for "{tag}".
      </div>
    );
  }

  return (
    <div className="px-4 py-3">
      <div className="text-xs text-muted-foreground mb-2">
        Showing {(page - 1) * limit + 1}-{Math.min(page * limit, data.total)} of {data.total} products
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs">#</TableHead>
            <TableHead className="text-xs">Product Name</TableHead>
            <TableHead className="text-xs">Price</TableHead>
            <TableHead className="text-xs">Company</TableHead>
            <TableHead className="text-xs">MOQ</TableHead>
            <TableHead className="text-xs">TA</TableHead>
            <TableHead className="text-xs">Status</TableHead>
            <TableHead className="text-xs">URL</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.items.map((product, idx) => (
            <ProductRow
              key={product.id}
              product={product}
              rank={(page - 1) * limit + idx + 1}
            />
          ))}
        </TableBody>
      </Table>
      {/* Pagination */}
      {data.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-3">
          <Button
            variant="outline" size="sm" className="h-7 text-xs"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            Prev
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {data.page} / {data.total_pages}
          </span>
          <Button
            variant="outline" size="sm" className="h-7 text-xs"
            disabled={page >= data.total_pages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Product Row ──

function ProductRow({ product, rank }: { product: ProductListItem; rank: number }) {
  const statusColor = product.parse_status === "parsed"
    ? "bg-green-100 text-green-800"
    : product.parse_status === "partial"
      ? "bg-yellow-100 text-yellow-800"
      : "bg-red-100 text-red-800";

  const priceText = product.price_min != null
    ? product.price_max != null && product.price_max !== product.price_min
      ? `$${product.price_min.toFixed(2)} - $${product.price_max.toFixed(2)}`
      : `$${product.price_min.toFixed(2)}`
    : "—";

  return (
    <TableRow className="text-xs">
      <TableCell className="py-1.5">{rank}</TableCell>
      <TableCell className="py-1.5 max-w-[250px] truncate" title={product.product_name || ""}>
        {product.product_name || "—"}
      </TableCell>
      <TableCell className="py-1.5 whitespace-nowrap">{priceText}</TableCell>
      <TableCell className="py-1.5 max-w-[150px] truncate">
        {product.company_name || "—"}
        {product.company_country && (
          <span className="text-muted-foreground ml-1">({product.company_country})</span>
        )}
      </TableCell>
      <TableCell className="py-1.5">{product.moq_text || "—"}</TableCell>
      <TableCell className="py-1.5">{product.has_trade_assurance ? "\u2705" : "—"}</TableCell>
      <TableCell className="py-1.5">
        <span className={`px-1.5 py-0.5 rounded text-[10px] ${statusColor}`}>
          {product.parse_status}
        </span>
      </TableCell>
      <TableCell className="py-1.5">
        <a
          href={product.product_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:underline inline-flex items-center gap-0.5"
          onClick={e => e.stopPropagation()}
        >
          <Globe className="h-3 w-3" />
        </a>
      </TableCell>
    </TableRow>
  );
}
